import React, { useState, useEffect } from 'react';
import {
  TextField,
  CircularProgress,
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  InputAdornment,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  ReceiptLong as ReceiptLongIcon,
  LockOpen as ReleaseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import GirviPageShell from '../common/GirviPageShell';
import DetailDialog, {
  DetailSection,
  DetailFieldGrid,
  DetailHeaderPhotos,
  DetailPhotos,
  money,
  listFilterFieldSx,
  listTableHeadSx,
  listActionBtnSx,
} from '../common/DetailDialog';
import PhotoThumb from '../common/PhotoThumb';
import baseURL from '../../baseURL';
import { computeArticleTypeWeightTotals, formatWeightGrams3 } from '../../utils/pledgeArticleWeights';
import { DEFAULT_DHARA, formatDhara } from '../../constants/dhara';
import { pledgeDisplayPhotos } from '../../utils/pledgePhotoUrl';
import { interestTillDate } from '../../utils/dharaInterest';
import CreateReceipt from './receipt/CreateReceipt';
import ReleasePage from './ReleasePage';
import { formatArticleMasterRate } from '../../utils/articleMasterRate';

const AllPledges = () => {
  const navigate = useNavigate();
  const [pledges, setPledges] = useState([]);
  const [filteredPledges, setFilteredPledges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState(-1);
  const [selectedPledge, setSelectedPledge] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pledgeToDelete, setPledgeToDelete] = useState(null);
  const [masterArticles, setMasterArticles] = useState([]);
  const [actionModal, setActionModal] = useState(null);

  useEffect(() => {
    fetchPledges();
  }, []);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${baseURL}/articles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && Array.isArray(data)) setMasterArticles(data);
        else if (Array.isArray(data?.data)) setMasterArticles(data.data);
      } catch (e) {
        console.error('Failed to load article master for pledge details:', e);
      }
    };
    fetchArticles();
  }, []);

  const fetchPledges = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/pledges/pledges`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      // Log the complete fetch data
      console.log('Fetched Pledges Data:', data);
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch pledges');
      }

      if (data.success) {
        setPledges(data.data || []);
        setFilteredPledges(data.data || []);  
        setSearchTerm('');
        setDateFrom('');
        setDateTo('');
      } else {
        setPledges([]);
        setFilteredPledges([]);
      }
    } catch (error) {
      console.error('Error fetching pledges:', error);
      setPledges([]);
      setFilteredPledges([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    applyFilters();
  };

  const handleDateChange = (type) => (event) => {
    const date = event.target.value;
    if (type === 'from') {
      setDateFrom(date);
    } else {
      setDateTo(date);
    }
    applyFilters();
  };

  // Helper function to format date as dd/mm/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const applyFilters = () => {
    let filtered = [...pledges];

    if (searchTerm) {
      const searchRegex = new RegExp(searchTerm, 'i');
      filtered = filtered.filter(pledge => 
        searchRegex.test(pledge.customer?.firstName || '') ||
        searchRegex.test(pledge.customer?.lastName || '') ||
        searchRegex.test(pledge.customer?.code || '') ||
        searchRegex.test(pledge.loanDetails?.ticketNo || '') ||
        searchRegex.test(pledge.customer?.mobileNo || '') ||
        searchRegex.test(pledge.customer?.village || '') ||
        searchRegex.test(String(pledge.loanDetails?.principalAmount || ''))
      );
    }

    if (dateFrom || dateTo) {
      // Normalize dates to start/end of day for accurate comparison
      let fromDate = null;
      let toDate = null;
      
      if (dateFrom) {
        fromDate = new Date(dateFrom);
        fromDate.setHours(0, 0, 0, 0);
      }
      
      if (dateTo) {
        toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
      }
      
      filtered = filtered.filter(pledge => {
        if (!pledge.date) return false;
        const pledgeDate = new Date(pledge.date);
        // Normalize pledge date to start of day for comparison
        pledgeDate.setHours(0, 0, 0, 0);
        
        if (fromDate && pledgeDate < fromDate) return false;
        if (toDate && pledgeDate > toDate) return false;
        return true;
      });
    }

    if (sortField && sortOrder) {
      filtered.sort((a, b) => {
        const aValue = sortField.includes('.') 
          ? sortField.split('.').reduce((obj, key) => obj[key], a)
          : a[sortField];
        const bValue = sortField.includes('.') 
          ? sortField.split('.').reduce((obj, key) => obj[key], b)
          : b[sortField];
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortOrder * aValue.localeCompare(bValue);
        }
        return sortOrder * (aValue - bValue);
      });
    }

    setFilteredPledges(filtered);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === -1 ? 1 : -1);
    } else {
      setSortField(field);
      setSortOrder(-1);
    }
    applyFilters();
  };

  const sortMark = (field) =>
    sortField === field ? (sortOrder === -1 ? ' ▼' : ' ▲') : '';

  const handleViewDetails = async (pledge) => {
    setSelectedPledge(pledge);
    setOpenDetails(true);
    // Refresh full pledge so hydrated / latest photos appear in the eye modal
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/pledges/${pledge._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data?.pledge) {
        setSelectedPledge(data.pledge);
        setPledges((prev) => prev.map((p) => (p._id === data.pledge._id ? { ...p, ...data.pledge } : p)));
        setFilteredPledges((prev) => prev.map((p) => (p._id === data.pledge._id ? { ...p, ...data.pledge } : p)));
      }
    } catch (e) {
      console.error('Failed to refresh pledge details:', e);
    }
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
  };

  const handleEdit = (pledge) => {
    navigate(`/pledge/create?edit=${pledge._id}`);
  };

  const handleDelete = (pledge) => {
    setPledgeToDelete(pledge);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!pledgeToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/pledges/${pledgeToDelete._id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Refresh the pledges list
        fetchPledges();
        setDeleteConfirmOpen(false);
        setPledgeToDelete(null);
      } else {
        alert(data.message || 'Failed to delete pledge');
      }
    } catch (error) {
      console.error('Error deleting pledge:', error);
      alert('Error deleting pledge');
    }
  };

  const handlePrint = async (pledge) => {
    try {
      const { printDocument } = await import('../../utils/printUtility');
      const { PledgePrint } = await import('../../components/print/PrintTemplates');
      await printDocument('pledge', pledge, PledgePrint);
    } catch (error) {
      console.error('Error printing pledge:', error);
      alert('Error printing document. Please try again.');
    }
  };

  return (
    <>
      <GirviPageShell
        title="All Pledges"
        subtitle="Pledge · List"
        chipLabel="Pledge"
        maxWidth="xl"
      >
        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            p: { xs: 1.5, md: 2 },
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            bgcolor: '#f8fafc',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Name, code, ticket, mobile, village, amount"
                sx={listFilterFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Date From"
                type="date"
                value={dateFrom}
                onChange={handleDateChange('from')}
                InputLabelProps={{ shrink: true }}
                sx={listFilterFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Date To"
                type="date"
                value={dateTo}
                onChange={handleDateChange('to')}
                InputLabelProps={{ shrink: true }}
                sx={listFilterFieldSx}
              />
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              overflow: 'auto',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={listTableHeadSx} onClick={() => handleSort('date')}>
                    Date{sortMark('date')}
                  </TableCell>
                  <TableCell sx={listTableHeadSx} onClick={() => handleSort('ticketNumber')}>
                    Ticket{sortMark('ticketNumber')}
                  </TableCell>
                  <TableCell sx={listTableHeadSx}>Photos</TableCell>
                  <TableCell sx={listTableHeadSx} onClick={() => handleSort('customer.code')}>
                    Code{sortMark('customer.code')}
                  </TableCell>
                  <TableCell sx={listTableHeadSx} onClick={() => handleSort('customer.name')}>
                    Customer{sortMark('customer.name')}
                  </TableCell>
                  <TableCell sx={listTableHeadSx} onClick={() => handleSort('loanDetails.principalAmount')} align="right">
                    Amount{sortMark('loanDetails.principalAmount')}
                  </TableCell>
                  <TableCell sx={{ ...listTableHeadSx, cursor: 'default' }} align="right">
                    Interest till date
                  </TableCell>
                  <TableCell sx={{ ...listTableHeadSx, cursor: 'default' }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPledges.length > 0 ? (
                  filteredPledges.map((pledge) => (
                    <TableRow
                      key={pledge._id}
                      hover
                      sx={{ '&:last-child td': { borderBottom: 0 } }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap', color: '#475569', fontWeight: 600 }}>
                        {formatDate(pledge.date)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={pledge.loanDetails?.ticketNo || 'N/A'}
                          sx={{
                            fontWeight: 800,
                            bgcolor: 'rgba(201,162,77,0.14)',
                            color: '#8a6d2f',
                            border: '1px solid rgba(201,162,77,0.35)',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          {(() => {
                            const photos = pledgeDisplayPhotos(pledge);
                            return (
                              <>
                                <PhotoThumb src={photos.clientPhoto} label="Client" size={36} />
                                <PhotoThumb src={photos.articlePhoto} label="Article" size={36} />
                              </>
                            );
                          })()}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {pledge.customer?.code || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {`${pledge.customer?.firstName || ''} ${pledge.customer?.lastName || ''}`.trim() || 'N/A'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap' }}>
                        {money(pledge.loanDetails?.principalAmount)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#1e3a5f', whiteSpace: 'nowrap' }}>
                        {money(interestTillDate(pledge, new Date(), DEFAULT_DHARA))}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          <Tooltip title="Receipt">
                            <span>
                              <IconButton
                                onClick={() => setActionModal({ type: 'receipt', ticket: pledge.loanDetails?.ticketNo })}
                                disabled={pledge.status === 'RELEASED' || pledge.loanDetails?.released || !pledge.loanDetails?.ticketNo}
                                sx={listActionBtnSx}
                                size="small"
                              >
                                <ReceiptLongIcon sx={{ fontSize: 18, color: '#0b1220' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Release">
                            <span>
                              <IconButton
                                onClick={() => setActionModal({ type: 'release', ticket: pledge.loanDetails?.ticketNo })}
                                disabled={pledge.status === 'RELEASED' || pledge.loanDetails?.released || !pledge.loanDetails?.ticketNo}
                                sx={listActionBtnSx}
                                size="small"
                              >
                                <ReleaseIcon sx={{ fontSize: 18, color: '#a16207' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="View">
                            <IconButton onClick={() => handleViewDetails(pledge)} sx={listActionBtnSx} size="small">
                              <VisibilityIcon sx={{ fontSize: 18, color: '#1e3a5f' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton onClick={() => handleEdit(pledge)} sx={listActionBtnSx} size="small">
                              <EditIcon sx={{ fontSize: 18, color: '#15803d' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton onClick={() => handleDelete(pledge)} sx={listActionBtnSx} size="small">
                              <DeleteIcon sx={{ fontSize: 18, color: '#b91c1c' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Print">
                            <IconButton onClick={() => handlePrint(pledge)} sx={listActionBtnSx} size="small">
                              <PrintIcon sx={{ fontSize: 18, color: '#0369a1' }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5, color: '#94a3b8' }}>
                      No pledges found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </GirviPageShell>

      <DetailDialog
        open={openDetails}
        onClose={handleCloseDetails}
        title={`Ticket ${selectedPledge?.loanDetails?.ticketNo || '—'}`}
        subtitle="Pledge details"
        status={selectedPledge?.status}
        statusColor={selectedPledge?.status === 'RELEASED' || selectedPledge?.status === 'closed' ? 'default' : 'success'}
        headerPhotos={
          (() => {
            const photos = pledgeDisplayPhotos(selectedPledge);
            return (
              <DetailHeaderPhotos
                clientPhoto={photos.clientPhoto}
                articlePhoto={photos.articlePhoto}
              />
            );
          })()
        }
      >
        {selectedPledge && (
          <>
            {(() => {
              const photos = pledgeDisplayPhotos(selectedPledge);
              return (
                <DetailPhotos
                  clientPhoto={photos.clientPhoto}
                  articlePhoto={photos.articlePhoto}
                  fingerprint={!!selectedPledge.fingerprint?.template || !!selectedPledge.fingerprint?.image}
                  size={110}
                />
              );
            })()}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <DetailSection title="Customer">
                  <DetailFieldGrid
                    items={[
                      { label: 'Code', value: selectedPledge.customer?.code || 'N/A' },
                      {
                        label: 'Name',
                        value: `${selectedPledge.customer?.firstName || ''} ${selectedPledge.customer?.lastName || ''}`.trim() || 'N/A',
                      },
                      { label: 'Date', value: formatDate(selectedPledge.date) },
                    ]}
                  />
                </DetailSection>
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailSection title="Loan">
                  <DetailFieldGrid
                    items={[
                      { label: 'Principal', value: money(selectedPledge.loanDetails?.principalAmount) },
                      { label: 'Interest till date', value: money(interestTillDate(selectedPledge, new Date(), DEFAULT_DHARA)) },
                      { label: 'Duration', value: `${selectedPledge.loanDetails?.duration || 'N/A'} months` },
                      { label: 'Rate', value: formatArticleMasterRate(masterArticles, selectedPledge) },
                      ...(selectedPledge.loanDetails?.dhara
                        ? [{ label: 'Dhara', value: formatDhara(selectedPledge.loanDetails.dhara) }]
                        : []),
                      { label: 'Paid excess', value: selectedPledge.loanDetails?.paidExcess ? 'Yes' : 'No' },
                    ]}
                  />
                </DetailSection>
              </Grid>
            </Grid>

            <DetailSection title="Articles">
              <TableContainer sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={listTableHeadSx}>Description</TableCell>
                      <TableCell sx={listTableHeadSx} align="right">Value</TableCell>
                      <TableCell sx={listTableHeadSx} align="right">Gross</TableCell>
                      <TableCell sx={listTableHeadSx} align="right">Net</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedPledge.articles?.length ? selectedPledge.articles.map((article, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontWeight: 600 }}>{article.description || 'N/A'}</TableCell>
                        <TableCell align="right">{money(article.valueTotal)}</TableCell>
                        <TableCell align="right">{formatWeightGrams3(article.grossWeight || article.grossWt)} g</TableCell>
                        <TableCell align="right">{formatWeightGrams3(article.netWeight || article.netWt)} g</TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ color: '#94a3b8', py: 3 }}>
                          No articles
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                {(() => {
                  const typeTotals = computeArticleTypeWeightTotals(selectedPledge.articles, masterArticles);
                  if (typeTotals.length >= 2) {
                    return typeTotals.map((t, idx) => (
                      <Typography key={idx} variant="body2" fontWeight={600}>
                        {t.title} gross: {formatWeightGrams3(t.totalGrossWeight)} g
                      </Typography>
                    ));
                  }
                  const gross = selectedPledge.totalGrossWeight
                    ? parseFloat(selectedPledge.totalGrossWeight)
                    : (selectedPledge.articles || []).reduce((s, a) => s + parseFloat(a.grossWeight || a.grossWt || 0), 0);
                  const net = selectedPledge.totalNetWeight
                    ? parseFloat(selectedPledge.totalNetWeight)
                    : (selectedPledge.articles || []).reduce((s, a) => s + parseFloat(a.netWeight || a.netWt || 0), 0);
                  return (
                    <>
                      <Typography variant="body2" fontWeight={600}>Total gross: {formatWeightGrams3(gross)} g</Typography>
                      <Typography variant="body2" fontWeight={600}>Total net: {formatWeightGrams3(net)} g</Typography>
                    </>
                  );
                })()}
              </Box>
            </DetailSection>
          </>
        )}
      </DetailDialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this pledge? This action cannot be undone.
            {pledgeToDelete && (
              <Box sx={{ mt: 1.5 }}>
                <strong>Ticket No:</strong> {pledgeToDelete.loanDetails?.ticketNo || 'N/A'}<br />
                <strong>Customer:</strong> {pledgeToDelete.customer?.firstName || ''} {pledgeToDelete.customer?.lastName || ''}
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(actionModal)}
        onClose={() => {
          setActionModal(null);
          fetchPledges();
        }}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid rgba(212,175,106,0.45)',
            boxShadow: '0 24px 60px rgba(11,18,32,0.35)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(180deg, #0b1220 0%, #1a2d4a 100%)',
            color: '#f5e6c8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            py: 1.25,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 11, letterSpacing: '0.14em', fontWeight: 800, color: 'rgba(245,230,200,0.7)' }}>
              {actionModal?.type === 'release' ? 'RELEASE' : 'RECEIPT'}
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.2 }}>
              Ticket {actionModal?.ticket || ''}
            </Typography>
          </Box>
          <Button
            onClick={() => {
              setActionModal(null);
              fetchPledges();
            }}
            sx={{ color: '#f5e6c8', fontWeight: 800, textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogTitle>
        <DialogContent sx={{ p: 1.25, bgcolor: '#f4f1ea' }}>
          {actionModal?.type === 'receipt' && (
            <CreateReceipt key={`receipt-${actionModal.ticket}`} embedded initialTicket={actionModal.ticket} />
          )}
          {actionModal?.type === 'release' && (
            <ReleasePage key={`release-${actionModal.ticket}`} embedded initialTicket={actionModal.ticket} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AllPledges;
